import { useCallback, useEffect, useState } from "react";
import { QueryBody } from "@api/internal/utils/types";
import useMyBtStore from "@features/mybt/store/useMyBTStore";
import { myBTRecommendedKeywords } from "@features/mybt/utlis/constants";
import { FeedTag, FeedTagResponseData } from "@routes/Keyword/utils/types";
import { tryCatch } from "@typings/TryCatch";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import axios, { HttpStatusCode } from "axios";

const domain = getEnvironmentDomain(getEnvironment());

/**
 * getMyBTList is a hook that fetches the MyBT list from the API and stores it in the state.
 */
export const useGetMyBTList = () => {
  const [myBTList, setMyBTList] = useState<FeedTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [followedTagsStore] = useMyBtStore((store) => [store.tags]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const fetchTags = myBTRecommendedKeywords.map((tag) =>
      fetchTagsListingStories({
        urlPath: domain + tag.urlPath,
        size: 1,
      })
    );

    try {
      const responses = await Promise.all(fetchTags);

      const newMyBtArticleList = responses.map((response, index) => {
        if (!response.success) {
          return myBTRecommendedKeywords[index];
        }

        return response.data.entity.tag;
      });

      const newList = [
        ...followedTagsStore,
        ...myBTList,
        ...newMyBtArticleList,
      ];

      const unique = newList.filter(
        (item, index, self) =>
          index === self.findIndex((tag) => tag.urlPath === item.urlPath)
      );

      setMyBTList(unique);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error my bt article list", error);

      return [];
    } finally {
      setIsLoading(false);
    }
  }, [myBTList, followedTagsStore]);

  useEffect(() => {
    if (myBTList.length !== 0) return;
    fetchData();
  }, [fetchData, myBTList]);

  return {
    myBTList,
    isLoading,
  };
};

const fetchTagsListingStories = async ({
  urlPath,
}: QueryBody): Promise<FeedTagResponseData> => {
  const payload: QueryBody = {
    urlPath,
    size: 1,
  };

  const request = axios.get<FeedTagResponseData>(
    "/_plat/api/v1/articles/tags",
    { params: payload }
  );

  const { data, error } = await tryCatch(request);

  if (error) {
    return {
      success: false,
      error: { code: HttpStatusCode.BadRequest, message: error.message },
    };
  }

  return data.data;
};
